import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { toast } from 'sonner';
import Image from '@/components/ui/Image';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorState } from '@/components/ErrorState';
import {
  answeredCount,
  classifiedCount,
  deferredCount,
  initialStreamsState,
  streamsReducer,
} from '@/components/classification/streams.helpers';
import { hasUsableConnection } from '@/components/onboarding/platformOnboarding';
import { useItems } from '@/hooks/queries/useItems';
import { useOnboardingState } from '@/hooks/queries/useOnboardingState';
import { useProfile } from '@/hooks/queries/useProfile';
import { useStreamCandidates } from '@/hooks/queries/useStreamCandidates';
import { useAuthedFetcher } from '@/hooks/queries/swrConfig';
import { ApiError, onboardingApi } from '@/lib/api';
import { track } from '@/lib/telemetry';
import { resumeStepFromServer, STEP } from './flow.helpers';
import { OnboardingStepper } from './OnboardingStepper';
import { StepConnections } from './StepConnections';
import { StepDone } from './StepDone';
import { StepStreams } from './StepStreams';
import { StepValidation } from './StepValidation';
import { requiredConsentGranted } from './validation.helpers';

export function OnboardingFlow() {
  const navigate = useNavigate();
  const { user } = useUser();
  const authedFetch = useAuthedFetcher();
  const {
    data: serverState,
    error: stateError,
    isLoading,
    mutate: mutateState,
  } = useOnboardingState();

  // The server position is the resume point; after hydration the local step
  // leads and every navigation writes through. `recurring` is carried for the
  // wire contract only — the Recorrentes step is retired, so the flow round-trips
  // whatever the server already holds and never edits it.
  const [step, setStep] = useState<number | null>(null);
  const [deferred, setDeferred] = useState<{ streams: boolean; recurring: boolean }>({
    streams: false,
    recurring: false,
  });
  const hydrated = useRef(false);

  useEffect(() => {
    if (!serverState || hydrated.current) return;
    hydrated.current = true;
    setStep(resumeStepFromServer(serverState.currentStep));
    setDeferred({
      streams: serverState.categoriesDeferred,
      recurring: serverState.recurringDeferred,
    });
  }, [serverState]);

  const [streamsState, streamsDispatch] = useReducer(streamsReducer, initialStreamsState);

  // Candidates load here (not only inside step 2) so a user resuming past the
  // step still gets a populated snapshot for the stepper and the summary.
  const { data: candidates } = useStreamCandidates();
  useEffect(() => {
    if (candidates) streamsDispatch({ type: 'streams_loaded', streams: candidates.streams });
  }, [candidates]);

  const { data: itemsData, isValidating: itemsValidating } = useItems();

  // Deleting a connection (step 1's Remover) must also remove its step-2 groups:
  // the streams snapshot survives server recomputes by design, so the deletion is
  // told to the reducer explicitly — with the DELETED item's own account ids.
  // Never derived from a "live" items read: useItems can legitimately serve
  // incomplete accounts, and pruning from that would drop groups for
  // connections that still exist (staging + review, 2026-08-22).
  const handleConnectionRemoved = useCallback((accountIds: string[]) => {
    streamsDispatch({ type: 'prune_accounts', accountIds });
  }, []);

  // The platform gate redirects here whenever the user lacks a USABLE connection
  // (its strict predicate: blocked LOGIN_ERROR/WAITING items don't count even with
  // accounts) — but the server resume position can point past step 1, e.g. a user
  // who finished onboarding and whose only connection later broke would land on
  // "Tudo pronto". Step 2+ is meaningless without a usable connection, so clamp
  // back to where repair lives. One-shot and local-only: the user's next
  // navigation writes the position through as usual.
  //
  // The one shot is consumed only once the in-flight revalidation settles:
  // useItems serves cached data synchronously while revalidating on mount, and a
  // stale usable snapshot must not suppress the clamp the fresh response calls for.
  // A resume position past Validação also means nothing while the identity
  // itself is unsatisfied (legacy user, revoked WhatsApp, consent never
  // recorded) — that check comes first inside the single clamp below.
  // ONE ordered clamp, not two one-shot effects: with separate effects both can
  // consume their shot in the same commit against the same stale `step`, and the
  // later (connection) clamp would override the earlier (validation) one —
  // sending a user with no CPF to Conexões, where the CPF self-guard just points
  // back here. Waiting on all three sources costs nothing: the flow needs each
  // of them before any step can render meaningfully.
  const { data: profile, mutate: mutateProfile } = useProfile();
  const clampChecked = useRef(false);
  useEffect(() => {
    if (
      clampChecked.current
      || step === null
      || profile === undefined
      || serverState === undefined
      || itemsData === undefined
      || itemsValidating
    ) return;
    clampChecked.current = true;
    const validationUnsatisfied =
      !profile.hasCpf
      || (profile.whatsAppVerificationRequired && !profile.hasWhatsAppUserId)
      || !requiredConsentGranted(serverState.consents);
    if (step > STEP.validation && validationUnsatisfied) {
      setStep(STEP.validation);
      return;
    }
    // Clamp to Conexões, NOT Validação: a user with valid CPF, WhatsApp and
    // consent whose only bank broke should land where repair lives, not be
    // asked to re-consent.
    if (step > STEP.connections && !hasUsableConnection(itemsData)) setStep(STEP.connections);
  }, [step, profile, serverState, itemsData, itemsValidating]);

  // Position writes are serialized: rapid navigation must not let an older PUT
  // land after a newer one and move the server resume point backwards.
  const positionWriteChain = useRef<Promise<unknown>>(Promise.resolve());
  const persistPosition = useCallback(
    (nextStep: number, nextDeferred: { streams: boolean; recurring: boolean }) => {
      setStep(nextStep);
      setDeferred(nextDeferred);
      positionWriteChain.current = positionWriteChain.current
        .then(() =>
          authedFetch((token) =>
            onboardingApi.updateState(token, {
              currentStep: nextStep,
              categoriesDeferred: nextDeferred.streams,
              recurringDeferred: nextDeferred.recurring,
            }),
          ),
        )
        .then((state) => mutateState(state, { revalidate: false }))
        .catch((err: unknown) => {
          // The position is a convenience — losing one write must not block the
          // flow, the next navigation retries.
          toast.error(err instanceof ApiError ? err.message : 'Não foi possível salvar o progresso.');
        });
    },
    [authedFetch, mutateState],
  );

  const goToConnections = useCallback(
    () => persistPosition(STEP.connections, deferred),
    [persistPosition, deferred],
  );
  const goToStreams = useCallback(
    () => persistPosition(STEP.streams, deferred),
    [persistPosition, deferred],
  );
  const continueFromValidation = useCallback(() => {
    // StepValidation already tracked its own completion after the writes landed.
    persistPosition(STEP.connections, deferred);
  }, [persistPosition, deferred]);
  const continueFromConnections = useCallback(() => {
    track('action.onboarding.step_completed', { step: STEP.connections, deferred: false });
    persistPosition(STEP.streams, deferred);
  }, [persistPosition, deferred]);
  const continueFromStreams = useCallback(() => {
    track('action.onboarding.step_completed', { step: STEP.streams, deferred: false });
    persistPosition(STEP.done, { ...deferred, streams: false });
  }, [persistPosition, deferred]);
  const skipStreams = useCallback(() => {
    track('action.onboarding.step_completed', { step: STEP.streams, deferred: true });
    persistPosition(STEP.done, { ...deferred, streams: true });
  }, [persistPosition, deferred]);

  const handleCompleted = useCallback(() => {
    void mutateState();
    // The platform gate decides needsFirstRunSetup from the long-lived
    // ['user-profile'] cache; navigating while it still says
    // onboardingCompletedAt: null (30s dedupe) bounces the user straight back
    // here. Stamp it optimistically; the background revalidation confirms.
    void mutateProfile(
      (current) => (current ? { ...current, onboardingCompletedAt: new Date().toISOString() } : current),
      { revalidate: true },
    );
    navigate('/', { replace: true });
  }, [mutateProfile, mutateState, navigate]);

  const streamsTotal = streamsState.streams.length;
  const streamsAddressed = answeredCount(streamsState);
  const streamsClassified = classifiedCount(streamsState);
  const streamsDeferred = deferredCount(streamsState);

  const isDone = useCallback(
    (n: number) => {
      if (step === null) return false;
      switch (n) {
        case STEP.validation:
          return (
            profile !== undefined
            && profile.hasCpf
            && (!profile.whatsAppVerificationRequired || profile.hasWhatsAppUserId)
            && requiredConsentGranted(serverState?.consents)
          );
        case STEP.connections:
          // The gate's strict predicate — a blocked item with accounts is not done.
          return hasUsableConnection(itemsData ?? []);
        case STEP.streams:
          // An empty worklist (loaded, nothing to ask) is a finished step.
          return (
            deferred.streams ||
            (candidates !== undefined && streamsTotal === 0) ||
            (streamsTotal > 0 && streamsAddressed >= streamsTotal)
          );
        default:
          return false;
      }
    },
    [
      step,
      profile,
      serverState,
      itemsData,
      deferred,
      candidates,
      streamsTotal,
      streamsAddressed,
    ],
  );
  const isDeferred = useCallback(
    (n: number) => n === STEP.streams && deferred.streams,
    [deferred],
  );

  const handleRetryState = useCallback(() => {
    void mutateState();
  }, [mutateState]);

  // Moving between steps replaces the whole panel: without a focus hand-off the
  // focus dies with the clicked Continuar/Voltar and screen readers announce
  // nothing. Focus the fresh panel (its heading is the first thing read) — on
  // NAVIGATIONS only, never the initial render, which must not steal focus.
  const stepPanelRef = useRef<HTMLDivElement | null>(null);
  const previousStepRef = useRef<number | null>(null);
  useEffect(() => {
    const previous = previousStepRef.current;
    previousStepRef.current = step;
    if (step === null || previous === null || previous === step) return;
    stepPanelRef.current?.focus();
  }, [step]);

  // A failed resume read must not masquerade as loading — without the server
  // position there is no step to render, so surface the failure with a retry.
  if (step === null && !isLoading && stateError) {
    return (
      <OnboardingShell>
        <ErrorState
          title="Erro ao carregar a configuração"
          message="Não conseguimos carregar o seu progresso agora. Verifique sua internet e tente novamente."
          onAction={handleRetryState}
        />
      </OnboardingShell>
    );
  }

  if (isLoading || step === null) {
    return (
      <OnboardingShell>
        <div className="grid gap-3">
          <Skeleton className="h-6 w-full rounded-full" />
          <Skeleton className="h-48 w-full rounded-3xl" />
        </div>
      </OnboardingShell>
    );
  }

  const userName = user?.firstName ?? user?.fullName ?? 'você';

  return (
    <OnboardingShell>
      <OnboardingStepper currentStep={step} isDone={isDone} isDeferred={isDeferred} />

      <div ref={stepPanelRef} tabIndex={-1} className="outline-none">
        {step === STEP.validation && <StepValidation onContinue={continueFromValidation} />}

        {step === STEP.connections && (
          <StepConnections onContinue={continueFromConnections} onConnectionRemoved={handleConnectionRemoved} />
        )}

        {step === STEP.streams && (
          <StepStreams
            state={streamsState}
            dispatch={streamsDispatch}
            onBack={goToConnections}
            onContinue={continueFromStreams}
            onSkip={skipStreams}
          />
        )}

        {step === STEP.done && (
          <StepDone
            userName={userName}
            connectionsCount={(itemsData ?? []).length}
            streamsClassified={streamsClassified}
            streamsDeferred={streamsDeferred}
            streamsTotal={streamsTotal}
            categoriesDeferred={deferred.streams}
            onBack={goToStreams}
            onCompleted={handleCompleted}
          />
        )}
      </div>
    </OnboardingShell>
  );
}

function OnboardingShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-svh bg-background font-sans text-text-primary antialiased">
      <header className="sticky top-0 z-20 flex h-16 items-center gap-2 border-b border-border-subtle bg-surface-glass px-5 backdrop-blur-md">
        {/* Decorative: the adjacent span carries the name — a non-empty alt would
            make screen readers announce "Vértice" twice. */}
        <Image src="/favicon.png" alt="" width={26} height={26} />
        <span className="text-[15px] font-bold tracking-tight">Vértice</span>
      </header>
      <main className="mx-auto max-w-2xl px-5 pt-6 pb-10">{children}</main>
    </div>
  );
}
